import { ArrowUpRight, FilePlus2 } from "lucide-react";
import {
  useGetList,
  useLocaleState,
  useTranslate,
  type Identifier,
} from "ra-core";
import { Link } from "react-router";

import { formatMoney } from "../clients/clientModel";
import type { Company, Proposal } from "../types";
import { PROPOSAL_STATUS_COLORS, monthlyTotal } from "./proposalModel";

/** Proposals of one deal (deal page) or of every company (proposals page). */
export const ProposalsList = ({
  dealId,
  companyId,
}: {
  dealId?: Identifier;
  companyId?: Identifier;
}) => {
  const translate = useTranslate();
  const [locale = "es"] = useLocaleState();
  const { data: proposals = [], isPending } = useGetList<Proposal>(
    "proposals",
    {
      filter: dealId != null ? { deal_id: dealId } : {},
      sort: { field: "updated_at", order: "DESC" },
      pagination: { page: 1, perPage: 200 },
    },
  );
  const { data: companies = [] } = useGetList<Company>("companies", {
    pagination: { page: 1, perPage: 500 },
    sort: { field: "name", order: "ASC" },
  });
  const companyName = (id: Identifier) =>
    companies.find((company) => company.id === id)?.name ?? "";

  const createParams = new URLSearchParams();
  if (dealId != null) createParams.set("deal", String(dealId));
  if (companyId != null) createParams.set("company", String(companyId));

  return (
    <div className="flex flex-col gap-3">
      <Link
        to={`/proposals/new?${createParams.toString()}`}
        className="flex items-center gap-2 self-start rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground no-underline hover:opacity-90"
      >
        <FilePlus2 className="size-4" />
        {translate("resources.proposals.create")}
      </Link>
      {!isPending && proposals.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {translate("resources.proposals.empty")}
        </p>
      ) : null}
      <ul className="flex flex-col gap-2">
        {proposals.map((proposal) => {
          const color = PROPOSAL_STATUS_COLORS[proposal.status];
          return (
            <li key={proposal.id}>
              <Link
                to={`/proposals/${proposal.id}`}
                className="group flex items-center gap-3 rounded-2xl bg-secondary px-4 py-3 no-underline text-foreground hover:bg-accent"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">{proposal.title}</div>
                  <div className="text-xs text-muted-foreground">
                    {dealId == null
                      ? `${companyName(proposal.company_id)} · `
                      : ""}
                    {formatMoney(monthlyTotal(proposal.items), "COP", locale)}{" "}
                    {translate("resources.proposals.document.per_month")}
                  </div>
                </div>
                <span
                  className="rounded-full px-3 py-1 text-xs font-semibold"
                  style={{ background: `${color}26`, color }}
                >
                  {translate(`resources.proposals.statuses.${proposal.status}`)}
                </span>
                <ArrowUpRight className="size-4 text-muted-foreground group-hover:text-foreground" />
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export const ProposalsPage = () => {
  const translate = useTranslate();
  return (
    <div className="mt-6 flex flex-col gap-6 pb-8">
      <h1 className="text-3xl font-semibold tracking-tight">
        {translate("resources.proposals.name", { smart_count: 2 })}
      </h1>
      <section className="rounded-[22px] bg-card p-5">
        <ProposalsList />
      </section>
    </div>
  );
};

ProposalsPage.path = "/proposals";
